/**
 * Centralized Audit Log Service
 * Provides unified audit logging with Firestore persistence
 * SOC2 Compliance: CC6.2, CC8.1 - Centralized Audit Log Storage
 */

import { getFailoverAwareDatabase } from '../config/firebaseWithFailover';
import { collection, addDoc, query, where, getDocs, orderBy, limit, Timestamp, QueryConstraint } from 'firebase/firestore';
import Logger from './logger';

export interface AuditLogEntry {
  id?: string;
  timestamp: Date;
  userId?: string;
  userEmail?: string;
  action: string;
  resource: string;
  resourceId?: string;
  operation: 'create' | 'read' | 'update' | 'delete' | 'access' | 'authorize' | 'deauthorize' | 'export' | 'import' | 'other';
  result: 'success' | 'failure' | 'denied';
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'authentication' | 'authorization' | 'data_access' | 'data_modification' | 'system_config' | 'security' | 'compliance' | 'other';
  details?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
  companyId?: string;
  metadata?: Record<string, any>;
}

export interface AuditLogQuery {
  userId?: string;
  companyId?: string;
  action?: string;
  operation?: AuditLogEntry['operation'];
  category?: AuditLogEntry['category'];
  severity?: AuditLogEntry['severity'];
  result?: AuditLogEntry['result'];
  startDate?: Date;
  endDate?: Date;
  resource?: string;
  resourceId?: string;
  limit?: number;
}

export interface AuditLogStats {
  totalEntries: number;
  entriesByCategory: Record<string, number>;
  entriesBySeverity: Record<string, number>;
  entriesByResult: Record<string, number>;
  entriesByOperation: Record<string, number>;
  recentEntries: AuditLogEntry[];
  errorRate: number;
}

export interface CentralizedAuditLogConfig {
  enabled: boolean;
  collectionName: string;
  retentionDays: number;
  batchSize: number;
  enableCompression: boolean;
  enableEncryption: boolean;
  logLevel: 'all' | 'medium_and_above' | 'high_and_above' | 'critical_only';
}

class CentralizedAuditLogService {
  private static instance: CentralizedAuditLogService;
  private config: Required<CentralizedAuditLogConfig>;
  private logQueue: AuditLogEntry[] = [];
  private isProcessing: boolean = false;
  private processingInterval: NodeJS.Timeout | null = null;

  private constructor(config: Partial<CentralizedAuditLogConfig> = {}) {
    this.config = {
      enabled: config.enabled ?? true,
      collectionName: config.collectionName ?? 'auditLogs',
      retentionDays: config.retentionDays ?? 2555, // 7 years for SOC2
      batchSize: config.batchSize ?? 50,
      enableCompression: config.enableCompression ?? false,
      enableEncryption: config.enableEncryption ?? false,
      logLevel: config.logLevel ?? 'all'
    };

    // Don't start processing in constructor - wait for explicit initialize() call
    // This prevents Firebase access during build/prerender
  }

  /**
   * Get singleton instance
   */
  public static getInstance(config?: Partial<CentralizedAuditLogConfig>): CentralizedAuditLogService {
    if (!CentralizedAuditLogService.instance) {
      CentralizedAuditLogService.instance = new CentralizedAuditLogService(config);
    }
    return CentralizedAuditLogService.instance;
  }

  /**
   * Initialize the service
   */
  public initialize(config?: Partial<CentralizedAuditLogConfig>): void {
    // Skip during prerender/SSR (Firebase not initialized)
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || 
        /HeadlessChrome/i.test(navigator?.userAgent || '')) {
      Logger.info('Centralized Audit Log Service skipped during prerender');
      return;
    }

    if (config) {
      this.config = { ...this.config, ...config };
    }

    if (this.config.enabled && !this.processingInterval) {
      this.startProcessing();
      this.scheduleRetentionCleanup();
    }

    Logger.info('Centralized Audit Log Service initialized', {
      enabled: this.config.enabled,
      collectionName: this.config.collectionName,
      retentionDays: this.config.retentionDays
    });
  }

  /**
   * Log an audit entry
   */
  public async logEntry(entry: Omit<AuditLogEntry, 'id' | 'timestamp'>): Promise<void> {
    if (!this.config.enabled) {
      return;
    }

    // Check log level filter
    if (!this.shouldLog(entry.severity)) {
      return;
    }

    const auditEntry: AuditLogEntry = {
      ...entry,
      timestamp: new Date()
    };

    // Add to queue for batch processing
    this.logQueue.push(auditEntry);

    // If queue is getting large, process immediately
    if (this.logQueue.length >= this.config.batchSize) {
      await this.processQueue();
    }
  }

  /**
   * Check if entry should be logged based on log level
   */
  private shouldLog(severity: AuditLogEntry['severity']): boolean {
    switch (this.config.logLevel) {
      case 'all':
        return true;
      case 'medium_and_above':
        return ['medium', 'high', 'critical'].includes(severity);
      case 'high_and_above':
        return ['high', 'critical'].includes(severity);
      case 'critical_only':
        return severity === 'critical';
      default:
        return true;
    }
  }

  /**
   * Start processing queue
   */
  private startProcessing(): void {
    if (this.processingInterval) {
      return;
    }

    // Process queue every 5 seconds
    this.processingInterval = setInterval(() => {
      this.processQueue().catch(error => {
        Logger.error('Failed to process audit log queue', error instanceof Error ? error : new Error('Unknown error'));
      });
    }, 5000);

    Logger.info('Audit log queue processor started');
  }

  /**
   * Recursively sanitize nested objects/arrays to replace undefined with null
   * Firestore doesn't accept undefined values at any nesting level
   */
  private recursivelySanitize(value: any): any {
    // Handle null or undefined
    if (value === null || value === undefined) {
      return null;
    }

    // Handle arrays - recursively sanitize each element
    if (Array.isArray(value)) {
      return value.map(item => this.recursivelySanitize(item));
    }

    // Handle objects - recursively sanitize each property
    if (typeof value === 'object' && value.constructor === Object) {
      const sanitized: Record<string, any> = {};
      for (const [key, val] of Object.entries(value)) {
        // Skip undefined values entirely or convert to null
        if (val === undefined) {
          sanitized[key] = null;
        } else {
          sanitized[key] = this.recursivelySanitize(val);
        }
      }
      return sanitized;
    }

    // Handle primitive values (string, number, boolean, etc.) - return as-is
    return value;
  }

  /**
   * Sanitize audit log entry for Firestore
   * Firestore doesn't accept undefined values, so we replace them with null or appropriate defaults
   */
  private sanitizeEntryForFirestore(entry: AuditLogEntry): Record<string, any> {
    const sanitized: Record<string, any> = {
      timestamp: Timestamp.fromDate(entry.timestamp),
      action: entry.action,
      resource: entry.resource,
      operation: entry.operation,
      result: entry.result,
      severity: entry.severity,
      category: entry.category
    };

    // Replace undefined with null for optional fields (Firestore accepts null)
    // Use 'system' as default for userId when undefined (indicates system event)
    sanitized.userId = entry.userId ?? 'system';
    sanitized.userEmail = entry.userEmail ?? null;
    sanitized.resourceId = entry.resourceId ?? null;
    sanitized.ipAddress = entry.ipAddress ?? null;
    sanitized.userAgent = entry.userAgent ?? null;
    sanitized.sessionId = entry.sessionId ?? null;
    sanitized.companyId = entry.companyId ?? null;
    
    // Recursively sanitize nested objects to handle undefined values at any depth
    sanitized.details = entry.details ? this.recursivelySanitize(entry.details) : null;
    sanitized.metadata = entry.metadata ? this.recursivelySanitize(entry.metadata) : null;

    // Remove any remaining undefined values (defensive)
    Object.keys(sanitized).forEach(key => {
      if (sanitized[key] === undefined) {
        sanitized[key] = null;
      }
    });

    return sanitized;
  }

  /**
   * Process queued audit logs
   */
  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.logQueue.length === 0) {
      return;
    }

    // Skip during prerender/SSR (Firebase not initialized)
    if (typeof window === 'undefined' || typeof navigator === 'undefined' || 
        /HeadlessChrome/i.test(navigator?.userAgent || '')) {
      return;
    }

    this.isProcessing = true;

    let batch: AuditLogEntry[] = [];
    try {
      const db = getFailoverAwareDatabase();
      if (!db) {
        Logger.warn('Database not available for audit log processing');
        this.isProcessing = false;
        return;
      }
      batch = this.logQueue.splice(0, this.config.batchSize);

      const promises = batch.map(async (entry) => {
        try {
          // Sanitize entry before writing to Firestore (removes undefined values)
          const sanitizedEntry = this.sanitizeEntryForFirestore(entry);
          await addDoc(collection(db, this.config.collectionName), sanitizedEntry);
        } catch (error) {
          Logger.error('Failed to write audit log entry', error instanceof Error ? error : new Error('Unknown error'), {
            entry: entry.action
          });
        }
      });

      await Promise.allSettled(promises);

      Logger.debug(`Processed ${batch.length} audit log entries`);
    } catch (error) {
      Logger.error('Failed to process audit log queue', error instanceof Error ? error : new Error('Unknown error'));
      // Re-add entries to queue on failure
      if (batch.length > 0) {
        this.logQueue.unshift(...batch);
      }
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Query audit logs
   */
  public async queryLogs(queryParams: AuditLogQuery): Promise<AuditLogEntry[]> {
    try {
      const db = getFailoverAwareDatabase();
      const constraints: QueryConstraint[] = [];

      if (queryParams.userId) {
        constraints.push(where('userId', '==', queryParams.userId));
      }

      if (queryParams.companyId) {
        constraints.push(where('companyId', '==', queryParams.companyId));
      }

      if (queryParams.action) {
        constraints.push(where('action', '==', queryParams.action));
      }

      if (queryParams.operation) {
        constraints.push(where('operation', '==', queryParams.operation));
      }

      if (queryParams.category) {
        constraints.push(where('category', '==', queryParams.category));
      }

      if (queryParams.severity) {
        constraints.push(where('severity', '==', queryParams.severity));
      }

      if (queryParams.result) {
        constraints.push(where('result', '==', queryParams.result));
      }

      if (queryParams.resource) {
        constraints.push(where('resource', '==', queryParams.resource));
      }

      if (queryParams.resourceId) {
        constraints.push(where('resourceId', '==', queryParams.resourceId));
      }

      if (queryParams.startDate) {
        constraints.push(where('timestamp', '>=', Timestamp.fromDate(queryParams.startDate)));
      }

      if (queryParams.endDate) {
        constraints.push(where('timestamp', '<=', Timestamp.fromDate(queryParams.endDate)));
      }

      constraints.push(orderBy('timestamp', 'desc'));

      if (queryParams.limit) {
        constraints.push(limit(queryParams.limit));
      } else {
        constraints.push(limit(1000)); // Default limit
      }

      const q = query(collection(db, this.config.collectionName), ...constraints);
      const snapshot = await getDocs(q);

      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate() || new Date()
        } as AuditLogEntry;
      });
    } catch (error) {
      Logger.error('Failed to query audit logs', error instanceof Error ? error : new Error('Unknown error'));
      return [];
    }
  }

  /**
   * Get audit log statistics
   */
  public async getStats(startDate?: Date, endDate?: Date): Promise<AuditLogStats> {
    try {
      const queryParams: AuditLogQuery = {
        startDate,
        endDate,
        limit: 10000 // Get more entries for stats
      };

      const entries = await this.queryLogs(queryParams);

      const stats: AuditLogStats = {
        totalEntries: entries.length,
        entriesByCategory: {},
        entriesBySeverity: {},
        entriesByResult: {},
        entriesByOperation: {},
        recentEntries: entries.slice(0, 100),
        errorRate: 0
      };

      let failureCount = 0;

      entries.forEach(entry => {
        stats.entriesByCategory[entry.category] = (stats.entriesByCategory[entry.category] || 0) + 1;
        stats.entriesBySeverity[entry.severity] = (stats.entriesBySeverity[entry.severity] || 0) + 1;
        stats.entriesByResult[entry.result] = (stats.entriesByResult[entry.result] || 0) + 1;
        stats.entriesByOperation[entry.operation] = (stats.entriesByOperation[entry.operation] || 0) + 1;

        if (entry.result === 'failure') {
          failureCount++;
        }
      });

      stats.errorRate = entries.length > 0 ? (failureCount / entries.length) * 100 : 0;

      return stats;
    } catch (error) {
      Logger.error('Failed to get audit log statistics', error instanceof Error ? error : new Error('Unknown error'));
      return {
        totalEntries: 0,
        entriesByCategory: {},
        entriesBySeverity: {},
        entriesByResult: {},
        entriesByOperation: {},
        recentEntries: [],
        errorRate: 0
      };
    }
  }

  /**
   * Schedule retention cleanup
   */
  private scheduleRetentionCleanup(): void {
    // Run cleanup daily
    setInterval(() => {
      this.cleanupOldLogs().catch(error => {
        Logger.error('Failed to cleanup old audit logs', error instanceof Error ? error : new Error('Unknown error'));
      });
    }, 24 * 60 * 60 * 1000); // 24 hours

    // Run initial cleanup
    this.cleanupOldLogs().catch(error => {
      Logger.error('Failed to cleanup old audit logs', error instanceof Error ? error : new Error('Unknown error'));
    });
  }

  /**
   * Cleanup old audit logs based on retention policy
   */
  private async cleanupOldLogs(): Promise<void> {
    try {
      // Skip during prerender/SSR (Firebase not initialized)
      if (typeof window === 'undefined' || typeof navigator === 'undefined' || 
          /HeadlessChrome/i.test(navigator?.userAgent || '')) {
        return;
      }

      const db = getFailoverAwareDatabase();
      if (!db) {
        Logger.warn('Database not available for audit log cleanup');
        return;
      }

      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - this.config.retentionDays);

      const q = query(
        collection(db, this.config.collectionName),
        where('timestamp', '<', Timestamp.fromDate(cutoffDate)),
        limit(1000) // Process in batches
      );

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        Logger.debug('No old audit logs to cleanup');
        return;
      }

      // Note: Firestore doesn't support batch delete in client SDK
      // This would need to be done via Cloud Functions or Admin SDK
      Logger.info(`Found ${snapshot.size} audit log entries older than retention period`, {
        cutoffDate: cutoffDate.toISOString(),
        retentionDays: this.config.retentionDays
      });

      // For now, we log the cleanup requirement
      // Actual deletion should be handled by Cloud Functions
    } catch (error) {
      Logger.error('Failed to cleanup old audit logs', error instanceof Error ? error : new Error('Unknown error'));
    }
  }

  /**
   * Get configuration
   */
  public getConfig(): Readonly<CentralizedAuditLogConfig> {
    return { ...this.config };
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<CentralizedAuditLogConfig>): void {
    this.config = { ...this.config, ...config };

    if (this.config.enabled && !this.processingInterval) {
      this.startProcessing();
    } else if (!this.config.enabled && this.processingInterval) {
      if (this.processingInterval) {
        clearInterval(this.processingInterval);
        this.processingInterval = null;
      }
    }

    Logger.info('Centralized Audit Log Service configuration updated', this.config);
  }

  /**
   * Flush queue (for testing or shutdown)
   */
  public async flushQueue(): Promise<void> {
    await this.processQueue();
  }

  /**
   * Get queue size
   */
  public getQueueSize(): number {
    return this.logQueue.length;
  }
}

// Export singleton instance
const centralizedAuditLogService = CentralizedAuditLogService.getInstance();

export default centralizedAuditLogService;

